import { ZERO_WEI } from '@kwenta/sdk/constants'
import type { FuturesMarket, FuturesMarketAsset } from '@kwenta/sdk/types'
import { floorNumber, formatDollars, getDisplayAsset } from '@kwenta/sdk/utils'
import { wei } from '@kwenta/wei'
import { useRouter } from 'next/router'
import { memo, useCallback, useMemo } from 'react'
import styled from 'styled-components'

import SelectedIcon from 'assets/svg/futures/selected-fav.svg'
import CurrencyIcon from 'components/Currency/CurrencyIcon'
import { DRAG_HANDLE_CLASS_NAME } from 'components/GridLayout'
import { Body, NumericValue } from 'components/Text'
import { FlexDiv, FlexDivRowCentered } from 'components/layout/flex'
import ROUTES from 'constants/routes'
import {
	selectMarkPriceInfos,
	selectMarkets,
	selectPerpsProvider,
} from 'state/futures/common/selectors'
import { selectFavoriteMarkets } from 'state/futures/selectors'
import { useAppSelector } from 'state/hooks'
import { selectPreviousDayPrices } from 'state/prices/selectors'

const MarketFavorites = memo(() => {
	const router = useRouter()

	const perpsProvider = useAppSelector(selectPerpsProvider)
	const allMarkets = useAppSelector(selectMarkets) as FuturesMarket<string>[]
	const favMarketAssets = useAppSelector(selectFavoriteMarkets)
	const markPrices = useAppSelector(selectMarkPriceInfos)
	const pastPrices = useAppSelector(selectPreviousDayPrices)

	const getBasePriceRateInfo = useCallback(
		(asset: FuturesMarketAsset) => {
			return markPrices[asset]
		},
		[markPrices]
	)

	const getPastPrice = useCallback(
		(asset: string) =>
			pastPrices.find(
				(price) => price.synth.toUpperCase() === getDisplayAsset(asset)?.toUpperCase()
			),
		[pastPrices]
	)

	const onSelectMarket = useCallback(
		(asset: string) => {
			router.push(ROUTES.Markets.MarketPair(asset, perpsProvider))
		},
		[perpsProvider, router]
	)

	const onSelectFavMarkets = useCallback(() => {
		router.push(ROUTES.Dashboard.Markets('all_markets'))
	}, [router])

	const favMarkets = useMemo(
		() =>
			allMarkets
				.filter((m) => favMarketAssets.includes(m.asset))
				.map((m) => {
					const basePriceRate = getBasePriceRateInfo(m.asset)
					const pastPrice = getPastPrice(m.asset)

					const change =
						basePriceRate && pastPrice?.rate && wei(basePriceRate.price).gt(0)
							? wei(basePriceRate.price).sub(pastPrice?.rate).div(pastPrice.rate)
							: ZERO_WEI
					return {
						...m,
						price: formatDollars(basePriceRate?.price ?? '0', {
							suggestDecimalsForAsset: m.asset,
						}),
						change: change.toNumber(),
					}
				}),
		[allMarkets, favMarketAssets, getBasePriceRateInfo, getPastPrice]
	)

	return (
		<Container className={DRAG_HANDLE_CLASS_NAME}>
			<FlexDiv columnGap="15px">
				<div style={{ cursor: 'pointer' }} onClick={onSelectFavMarkets}>
					<SelectedIcon height={14} width={14} />
				</div>
				<MarketFavoritesContainer columnGap="15px">
					{favMarkets.map((market) => (
						<FavorityContainer
							key={market.asset}
							columnGap="8px"
							onClick={() => onSelectMarket(market.asset)}
						>
							<CurrencyIcon currencyKey={market.asset} width={18} height={18} />
							<Body size="medium">{getDisplayAsset(market.asset)}</Body>
							<Price mono size="medium">
								{market.price}
							</Price>
							<NumericValue
								size="medium"
								suffix="%"
								colored
								value={floorNumber(market.change ? market.change * 100 : '0', 2)}
							/>
						</FavorityContainer>
					))}
				</MarketFavoritesContainer>
			</FlexDiv>
		</Container>
	)
})

const Container = styled(FlexDivRowCentered)`
	height: 100%;
	padding: 0 15px;
	overflow-x: scroll;
`

const MarketFavoritesContainer = styled(FlexDivRowCentered)`
	& > div {
		border-right: 1px solid ${(props) => props.theme.colors.selectedTheme.newTheme.border.color};
	}

	& > div:last-child {
		border-right: none;
	}
`

const Price = styled(Body)`
	color: ${(props) => props.theme.colors.selectedTheme.text.label};
`

const FavorityContainer = styled(FlexDivRowCentered)`
	cursor: pointer;
	padding-right: 15px;
`

export default MarketFavorites
